// Message hover-action panel — Figma node `2804:27483`.
//
// Layout (left → right):
//   [❤️] [😂] [👍]   [😊 React]   [↩ Reply]   [⋮]
//
// All chips share the same `#262626` fill + `r=8` radius. The outer
// panel is `#15171C` + `r=20`. The 3 quick-reaction emojis tap-to-send
// the canonical `com.ping.reaction` envelope; `React` opens a full
// emoji picker; `Reply` sets the composer reply chip; the `⋮` Menu
// chip opens the secondary action dropdown (see
// `MessageActionMenu.tsx`).
//
// The panel itself lives inside the shared `<Popover>` so it:
//   • Anchors to the bubble in viewport-coordinate space
//   • Flips above ↔ below the bubble based on available room
//   • Horizontally clamps to the viewport (`screenPadding`)
//   • Portals to `document.body` on react-native-web so it escapes
//     any `overflow: hidden` parent

import React, { forwardRef, useState } from "react";
import {
  Pressable,
  StyleSheet,
  Text,
  type TextStyle,
  View,
} from "react-native";
import { useColorScheme } from "../foundations/colorScheme";
import { CornerUpLeft, MoreHorizontal, Pencil, Smile } from "lucide-react-native";
import { tokens } from "../design-system/tokens";

// Quick-react emoji order matches the Design-system hover menu
// (3093:91192 / 3992:75142): 👍 ✅ 😂.
const DEFAULT_QUICK_REACTIONS: ReadonlyArray<string> = ["👍", "✅", "😂"];

// Expanded reaction set shown when the "React" chip is tapped — a
// curated grid of the most common chat reactions. Selecting one toggles
// it through the same `onSelectQuickReaction` path as the quick row.
const REACTION_PICKER_EMOJIS: ReadonlyArray<string> = [
  "👍",
  "❤️",
  "😂",
  "😮",
  "😢",
  "🙏",
  "🎉",
  "🔥",
  "👏",
  "✅",
  "😍",
  "😡",
  "🤔",
  "👀",
  "💯",
  "🙌",
  "😅",
  "🥳",
];

export interface MessageInteractionPanelProps {
  /** Optional override for the 3-emoji quick row. Defaults to 👍 ✅ 😂. */
  readonly quickReactions?: ReadonlyArray<string> | undefined;
  readonly onSelectQuickReaction: (emoji: string) => void;
  /** Opens a full reaction picker — host decides what UI to show. */
  readonly onReact?: (() => void) | undefined;
  readonly onReply?: (() => void) | undefined;
  /** Edit this message. Supplied ONLY for the user's OWN text messages, so the
   *  chip appears (after Reply) only where editing is allowed. */
  readonly onEdit?: (() => void) | undefined;
  readonly onMore?: (() => void) | undefined;
  readonly testID?: string | undefined;
}

const PanelInner = forwardRef<View, MessageInteractionPanelProps>(
  function PanelInner(
    {
      quickReactions = DEFAULT_QUICK_REACTIONS,
      onSelectQuickReaction,
      onReact,
      onReply,
      onEdit,
      onMore,
      testID,
    },
    ref,
  ) {
    const scheme = useColorScheme();
    const colorScheme: "light" | "dark" = scheme === "dark" ? "dark" : "light";
    const colors = tokens.color.semantic[colorScheme];

    // Expanded emoji-grid picker, toggled by the "React" chip.
    const [showPicker, setShowPicker] = useState(false);

    // Panel + chip palette from Figma:
    //   panel fill  #15171C (rgb 21,23,28)
    //   chip fill   #262626 (rgb 38,38,38)
    //   label color #F5F6F8 (rgb 245,246,248)
    //   glyph color #8C92A0 (rgb 140,146,158) for the menu dots
    const PANEL_FILL = colorScheme === "dark" ? "#15171C" : "#F5F6F8";
    // const CHIP_FILL = colorScheme === "dark" ? "#262626" : "#E5E7EB";
    const LABEL_COLOR = colorScheme === "dark" ? "#F5F6F8" : "#0B0C0F";
    const GLYPH_COLOR = "#8C92A0";

    const labelStyle: TextStyle = {
      color: LABEL_COLOR,
      fontFamily: tokens.typography.fontFamily.base,
      fontSize: 13,
      fontWeight: "500",
      lineHeight: 17,
    };

    return (
      <View
        ref={ref}
        style={styles.wrapper}
        accessibilityLabel="Message actions"
        testID={testID}
      >
        {/* Expanded reaction-picker grid — revealed by the React chip.
            Sits as its own card above the chip row. Selecting an emoji
            toggles it through the same path as the quick-react row. */}
        {showPicker && onReact !== undefined ? (
          <View
            style={[
              styles.pickerCard,
              {
                backgroundColor: PANEL_FILL,
                borderColor: colors.border.secondary,
              },
            ]}
            accessibilityLabel="Reaction picker"
          >
            {REACTION_PICKER_EMOJIS.map((emoji) => (
              <Pressable
                key={emoji}
                onPress={() => {
                  onSelectQuickReaction(emoji);
                  setShowPicker(false);
                }}
                accessibilityRole="button"
                accessibilityLabel={`React with ${emoji}`}
                style={[styles.chip, styles.emojiChip]}
              >
                <Text style={styles.emoji}>{emoji}</Text>
              </Pressable>
            ))}
          </View>
        ) : null}

        <View
          style={[
            styles.panel,
            {
              backgroundColor: PANEL_FILL,
              borderColor: colors.border.secondary,
            },
          ]}
        >
          {/* Quick reactions — Figma `fast reaction` group. */}
          {quickReactions.map((emoji) => (
            <Pressable
              key={emoji}
              onPress={() => onSelectQuickReaction(emoji)}
              accessibilityRole="button"
              accessibilityLabel={`React with ${emoji}`}
              style={[styles.chip, styles.emojiChip]}
            >
              <Text style={styles.emoji}>{emoji}</Text>
            </Pressable>
          ))}

          {/* React text chip — Figma `add-Reaction`. Toggles the
              expanded emoji grid above. */}
          {onReact !== undefined ? (
            <Pressable
              onPress={() => setShowPicker((v) => !v)}
              accessibilityRole="button"
              accessibilityLabel="Open reaction picker"
              style={[
                styles.chip,
                styles.labelChip,
                {
                  backgroundColor: showPicker
                    ? colors.accent.indigo + "33"
                    : 'transparent',
                },
              ]}
            >
              <Smile size={14} strokeWidth={1.75} color={LABEL_COLOR} />
              <Text style={labelStyle}>React</Text>
            </Pressable>
          ) : null}

          {/* Reply text chip — Figma `add-Reply`. */}
          {onReply !== undefined ? (
            <Pressable
              onPress={onReply}
              accessibilityRole="button"
              accessibilityLabel="Reply to message"
              style={[styles.chip, styles.labelChip]}
            >
              <CornerUpLeft size={14} strokeWidth={1.75} color={LABEL_COLOR} />
              <Text style={labelStyle}>Reply</Text>
            </Pressable>
          ) : null}

          {/* Edit text chip — own text messages only (host gates by passing
              `onEdit` only for self). Sits right after Reply. */}
          {onEdit !== undefined ? (
            <Pressable
              onPress={onEdit}
              accessibilityRole="button"
              accessibilityLabel="Edit message"
              style={[styles.chip, styles.labelChip]}
            >
              <Pencil size={14} strokeWidth={1.75} color={LABEL_COLOR} />
              <Text style={labelStyle}>Edit</Text>
            </Pressable>
          ) : null}

          {/* Menu chip (⋮) — Figma `Menu`. Opens the secondary action
              dropdown (Copy / Reply in Thread / Forward / Pin / Remove). */}
          {onMore !== undefined ? (
            <Pressable
              onPress={onMore}
              accessibilityRole="button"
              accessibilityLabel="More actions"
              style={[styles.chip, styles.menuChip]}
            >
              <MoreHorizontal
                size={14}
                strokeWidth={1.75}
                color={GLYPH_COLOR}
              />
            </Pressable>
          ) : null}
        </View>
      </View>
    );
  },
);

export const MessageInteractionPanel = React.memo(PanelInner);

const styles = StyleSheet.create({
  // Column wrapper holding the optional reaction-picker grid above the
  // chip row. `flex-start` keeps both cards hugging the same edge.
  wrapper: {
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 6,
  },
  // Expanded reaction grid — same card treatment as the panel, wraps
  // its emoji chips into rows with a sensible max width.
  pickerCard: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 6,
    padding: 8,
    borderRadius: 16,
    borderWidth: StyleSheet.hairlineWidth,
    maxWidth: 252,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.22,
    shadowRadius: 14,
    elevation: 6,
  },
  // Outer panel — Figma `Action panel `.
  //   r=20 / padding L8/R8/T4/B4 / horizontal gap 8 / height ~34
  panel: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 20,
    borderWidth: StyleSheet.hairlineWidth,
    // Soft elevation so the panel reads as floating over chat content.
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.22,
    shadowRadius: 14,
    elevation: 6,
  },
  chip: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 8,
  },
  // Quick-react emoji chip — Figma `Reaction`: 26x26 r=8 padding 4/4/4/4
  emojiChip: {
    width: 26,
    height: 26,
    justifyContent: "center",
  },
  // Labelled chip — Figma `add-Reaction` / `add-Reply`:
  // 56x25 r=8 padding 4/4/4/4 gap 4
  labelChip: {
    height: 25,
    gap: 4,
    padding: 4,
  },
  // Menu (⋮) chip — Figma `Menu`: 22x22 r=8 padding 4/4/4/4
  menuChip: {
    width: 22,
    height: 22,
    justifyContent: "center",
  },
  emoji: {
    fontSize: 20,
    lineHeight: 22,
  },
});
